import React, { useEffect, useRef, useState } from 'react'
import 'react-datepicker/dist/react-datepicker.css'
import './DateInput.scss'
import { FaCalendar } from 'react-icons/fa'
import DatePicker from 'react-datepicker'
import { BlurEvent, FieldError } from 'common.types'
import { FieldErrorList } from 'components/FieldErrorList/FieldErrorList'
import { getInputClasses } from 'utilities/utilities'
import { InputStatusIcon } from 'components/InputStatusIcon/InputStatusIcon'

interface DateInputProps {
  name: string
  label: string
  initialValue?: string
  required?: boolean
  onChange: (value: string) => void
  fixedTodaysDate?: boolean
  startDateOnClick?: Date
  maxDate?: Date
  onlyYear?: boolean
  onBlur?: (e: BlurEvent) => void
  errorList?: FieldError[]
}

export const DateInput: React.FC<DateInputProps> = ({
  label,
  name,
  initialValue,
  required,
  onChange,
  fixedTodaysDate,
  startDateOnClick,
  maxDate,
  onlyYear,
  onBlur,
  errorList,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [pickerState, setPickerState] = useState<'year' | 'month' | 'day'>(
    'day',
  )
  const [focused, setFocused] = useState(false)
  const ref = useRef()

  useEffect(() => {
    if (!onlyYear && (initialValue || fixedTodaysDate)) {
      setPickerState('day')
    }
    if (onlyYear) {
      setPickerState('year')
    }
  }, [])

  useEffect(() => {
    const handleClickOutside = (event: any) => {
      if (
        (ref.current as any) &&
        !(ref.current as any).contains(event.target)
      ) {
        setFocused(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [setFocused])

  const today = new Date()
  const maxDateToInput = maxDate ?? today
  const errorsExist = errorList && errorList.length > 0
  const inputEntered = initialValue || selectedDate ? true : false
  const requiredBarClass = 'date-input__input__required-bar'
  const requiredBarExtraClass = errorsExist
    ? `${requiredBarClass}__inner--error`
    : inputEntered
    ? `${requiredBarClass}__inner--input-enterred`
    : ''

  const formatDateToMMDDYYYY = (date: Date) => {
    const month = String(date.getMonth() + 1).padStart(2, '0') // Months are zero-indexed
    const day = String(date.getDate()).padStart(2, '0')
    const year = date.getFullYear()

    return `${month}/${day}/${year}`
  }

  const [dateString, setDateString] = useState(
    initialValue
      ? onlyYear
        ? initialValue
        : formatDateToMMDDYYYY(new Date(initialValue))
      : '',
  )

  const transformDayInput = (input: string) => {
    // Ensure the length is not more than 8 characters (MMDDYYYY)
    if (input.length > 8) {
      input = input.slice(0, 8)
    }

    let month = input.slice(0, 2)
    let day = input.slice(2, 4)
    let year = input.slice(4)

    // Validate month (01-12)
    if (month.length === 2) {
      const monthNumber = parseInt(month, 10)
      if (monthNumber < 1 || monthNumber > 12) {
        month = '12' // Set the month to 12 if out of range
      }
    }

    // Validate day based on month and year
    if (day.length === 2 && year.length >= 4) {
      const dayNumber = parseInt(day, 10)
      const monthNumber = parseInt(month, 10)
      const yearNumber = parseInt(year, 10)

      const daysInMonth = new Date(yearNumber, monthNumber, 0).getDate() // Get days in the month
      if (dayNumber < 1 || dayNumber > daysInMonth) {
        day = String(daysInMonth).padStart(2, '0') // Set to max valid day if out of range
      }
    } else if (day.length === 2) {
      const dayNumber = parseInt(day, 10)
      if (dayNumber < 1 || dayNumber > 31) {
        day = '31' // Set the day to 31 if out of range
      }
    }

    // Format the input as mm/dd/yyyy
    if (input.length > 4) {
      input = `${month}/${day}/${year}`
    } else if (input.length > 2) {
      input = `${month}/${day}`
    } else {
      input = month
    }
    return input
  }

  return (
    <div className="input-wrapper date-input">
      <label htmlFor={name} className="date-input__label">
        {required && (
          <span className="date-input__label__required-asterisk">*</span>
        )}
        {label}
      </label>
      <div
        className={`date-input__input ${getInputClasses(
          errorsExist,
          inputEntered,
        )}`}
        ref={ref as any}
      >
        {required && (
          <div className={requiredBarClass}>
            <div
              className={`${requiredBarClass}__inner${
                requiredBarExtraClass ? ' ' + requiredBarExtraClass : ''
              }`}
            ></div>
          </div>
        )}

        <DatePicker
          maxDate={maxDateToInput}
          className="date-input__input__sdk_picker"
          wrapperClassName="date-input__wrapper"
          name={name}
          onBlur={(e) => {
            if (!e.nativeEvent.relatedTarget) {
              setFocused(false)
            }
            if (onBlur) {
              onBlur(e)
            }
          }}
          disabled={fixedTodaysDate}
          onChange={(e) => {}}
          value={dateString}
          onChangeRaw={(e) => {
            if (!e.target.value && typeof e.target.value !== 'string') {
              return
            }
            let input = e.target.value.replace(/\D/g, '') // Remove non-numeric characters
            if (onlyYear) {
              if (input.length > 4) {
                input = input.slice(0, 4)
              }
            } else {
              input = transformDayInput(input)
            }
            setDateString(input)
            onChange(input)
          }}
          onSelect={(date) => {
            const input = onlyYear
              ? date.getFullYear().toString()
              : formatDateToMMDDYYYY(date)
            setDateString(input)
            setFocused(false)
            onChange(input)
          }}
          open={focused}
          placeholderText={onlyYear ? 'yyyy' : 'mm/dd/yyyy'}
          onCalendarOpen={() => {}}
          showYearPicker={pickerState === 'year'}
          dateFormat={pickerState === 'year' ? 'yyyy' : 'MM/dd/yyyy'}
        />
        <button
          className="date-input__input__calendar-icon"
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setFocused(!focused)
          }}
        >
          <FaCalendar />
        </button>
        <InputStatusIcon
          errorsExist={errorsExist}
          inputEntered={inputEntered}
        />
      </div>
      <FieldErrorList errorListForField={errorList} />
    </div>
  )
}
